import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Card, Col, Row, Table, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import useToken from 'hooks/useToken';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp, faExchangeAlt, faMoneyBillWave, faFingerprint } from '@fortawesome/free-solid-svg-icons';

const DocumentosRelacionados = ({ documentos, tipoFactura }) => {
    const { token } = useToken();
    const [uuidsExistentes, setUuidsExistentes] = useState({});

    useEffect(() => {
        if (!documentos || documentos.length === 0) return;

        const verificarUUIDs = async () => {
            try {
                const uuidsList = [];
                documentos.forEach(grupo => {
                    const lista = Array.isArray(grupo.CfdiRelacionado) ? grupo.CfdiRelacionado : (grupo.CfdiRelacionado ? [grupo.CfdiRelacionado] : []);
                    lista.forEach(doc => { if (doc.UUID) uuidsList.push(doc.UUID); });
                });

                if (uuidsList.length === 0) return;

                const endpoint = tipoFactura === 'emitida'
                    ? '/api/contabilidad/centro-xml/FacturasEmitidas/verificarUUIDs'
                    : '/api/contabilidad/centro-xml/FacturasRecibidas/verificarUUIDs';

                const response = await fetch(endpoint, {
                    method: 'POST',
                    headers: {
                        'Authorization': 'Bearer ' + token,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ uuids: uuidsList })
                });

                if (response.ok) {
                    const data = await response.json();
                    if (data.iResponseType === 1 && data.uuids) {
                        setUuidsExistentes(data.uuids);
                    }
                }
            } catch (error) {
                console.error("Error al verificar UUIDs", error);
            }
        };

        verificarUUIDs();
    }, [documentos, tipoFactura, token]);

    if (!documentos || documentos.length === 0) return null;

    const getTipoComprobanteConfig = (tipo) => {
        const configs = {
            I: { label: 'Ingreso', variant: 'primary', icon: faArrowDown },
            E: { label: 'Egreso', variant: 'danger', icon: faArrowUp },
            T: { label: 'Traslado', variant: 'info', icon: faExchangeAlt },
            P: { label: 'Pago', variant: 'success', icon: faMoneyBillWave }
        };
        return configs[tipo] || { label: 'Desconocido', variant: 'secondary', icon: faMoneyBillWave };
    };

    const formatNumberMX = (value) => {
        return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value);
    };

    const tipoRelacionMap = {
        '01': '01 - Nota de crédito de los documentos relacionados',
        '02': '02 - Nota de débito de los documentos relacionados',
        '03': '03 - Devolución de mercancía sobre facturas o traslados previos',
        '04': '04 - Sustitución de los CFDI previos',
        '05': '05 - Traslados de mercancías facturados previamente',
        '06': '06 - Factura generada por los traslados previos',
        '07': '07 - CFDI por aplicación de anticipo'
    };

    return (
        <Row className="mb-3">
            <Col md={12}>
                <Card className="shadow-sm border">
                    <Card.Body className="p-0">
                        <Table borderless className="mb-0 small" style={{ tableLayout: "fixed", width: "100%" }}>
                            <tbody>
                                <tr className="bg-light">
                                    <td className="text-dark fw-bold px-3 py-2" style={{ fontSize: "0.9rem", borderBottom: "1px solid #dee2e6" }}>
                                        Documentos Relacionados
                                    </td>
                                </tr>
                                {documentos.map((grupo, idx) => {
                                    // CfdiRelacionado puede ser array u objeto único
                                    const listaRelacionados = Array.isArray(grupo.CfdiRelacionado)
                                        ? grupo.CfdiRelacionado
                                        : grupo.CfdiRelacionado ? [grupo.CfdiRelacionado] : [];

                                    return (
                                        <tr key={idx} className="bg-white">
                                            <td className="text-dark px-3 py-2" style={{ fontSize: "0.85rem", borderBottom: "1px solid #dee2e6" }}>
                                                <div className="fw-bold text-secondary mb-1">
                                                    Tipo de Relación: {tipoRelacionMap[grupo.TipoRelacion] || grupo.TipoRelacion}
                                                </div>
                                                <div className="d-flex flex-column gap-2 mt-2">
                                                    {listaRelacionados.map((doc, docIdx) => {
                                                        const dataFactura = uuidsExistentes[doc.UUID];
                                                        const idFactura = dataFactura ? dataFactura.id : null;
                                                        const tipoConfig = dataFactura ? getTipoComprobanteConfig(dataFactura.tipo) : null;

                                                        return (
                                                            <div key={docIdx} className="d-flex align-items-center justify-content-between p-2 border rounded bg-light" style={{ transition: 'all 0.2s ease' }}>
                                                                <div>
                                                                    <span className="text-secondary fw-bold" style={{ fontSize: '0.8rem' }}><FontAwesomeIcon icon={faFingerprint} className="me-1 text-muted" />UUID: </span>
                                                                    
                                                                    {idFactura ? (
                                                                        <Link 
                                                                            to={`/contabilidad/centro-xml/facturas-${tipoFactura === 'emitida' ? 'emitidas' : 'recibidas'}/visualizacion/${idFactura}`}
                                                                            className="text-decoration-none d-inline-block"
                                                                            style={{ cursor: 'pointer', fontSize: '0.85rem' }}
                                                                        >
                                                                            <span className="d-inline-block text-primary font-monospace" style={{ textDecoration: 'underline' }}>
                                                                                {doc.UUID}
                                                                            </span>
                                                                        </Link>
                                                                    ) : (
                                                                        <span className="font-monospace text-dark" style={{ fontSize: '0.85rem' }}>{doc.UUID}</span>
                                                                    )}

                                                                    {dataFactura && (
                                                                        <span className="ms-2">
                                                                            <span className="text-muted">|</span> <span className="text-dark" style={{ fontSize: '0.85rem' }}>{dataFactura.fecha}</span> <span className="text-muted">|</span> <span className="text-dark fw-bold" style={{ fontSize: '0.85rem' }}>{formatNumberMX(dataFactura.total)}</span> <span className="text-muted">|</span> <Badge bg={tipoConfig.variant} className="ms-1 px-2 py-1"><FontAwesomeIcon icon={tipoConfig.icon} className="me-1" />{tipoConfig.label}</Badge>
                                                                        </span>
                                                                    )}

                                                                    {!idFactura && (
                                                                        <div className="text-danger mt-1" style={{ fontSize: '0.75rem' }}>
                                                                            <i className="bi bi-exclamation-circle-fill me-1"></i>
                                                                            Esta factura no está en Akasia Admin Panel
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    );
};

DocumentosRelacionados.propTypes = {
    documentos: PropTypes.array,
    tipoFactura: PropTypes.oneOf(['emitida', 'recibida']).isRequired
};

export default DocumentosRelacionados;
